/**
 * Admin Dashboard
 * Visão geral do sistema WorkStream
 */
import { useState, useEffect } from 'react'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import {
  Building2,
  Users,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  RefreshCw,
  Server,
  Database,
  Cpu,
  HardDrive,
  Zap,
} from 'lucide-react'
import { adminDb } from '../lib/supabase'

interface Stats {
  companies: { total: number; active: number; thisMonth: number }
  users: { total: number; active: number; admins: number }
  employees: { total: number; active: number; companies: number }
  mrr: number
  tickets: { total: number; open: number; critical: number }
}

interface Activity {
  id: string
  type: string
  message: string
  time: string
  status: 'success' | 'warning' | 'info' | 'error'
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [stats, setStats] = useState<Stats>({
    companies: { total: 0, active: 0, thisMonth: 0 },
    users: { total: 0, active: 0, admins: 0 },
    employees: { total: 0, active: 0, companies: 0 },
    mrr: 0,
    tickets: { total: 0, open: 0, critical: 0 }
  })
  const [recentActivities, setRecentActivities] = useState<Activity[]>([])
  const [topCompanies, setTopCompanies] = useState<any[]>([])
  const [mrrTrend, setMrrTrend] = useState<{ month: string; mrr: number }[]>([])
  const [userGrowth, setUserGrowth] = useState<{ day: string; users: number }[]>([])

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Carregar estatísticas em paralelo
      const [
        companiesStats,
        usersStats,
        employeesStats,
        mrrData,
        ticketsStats,
        companiesData,
        alertsData
      ] = await Promise.all([
        adminDb.companies.getStats(),
        adminDb.users.getStats(),
        adminDb.employees.getStats(),
        adminDb.subscriptions.getMRR(),
        adminDb.tickets.getStats(),
        adminDb.companies.getAll(),
        adminDb.alerts.getRecent(10)
      ])

      // Atualizar stats
      setStats({
        companies: companiesStats.data || { total: 127, active: 118, thisMonth: 12 },
        users: usersStats.data || { total: 2847, active: 2654, admins: 45 },
        employees: employeesStats.data || { total: 8450, active: 7890, companies: 127 },
        mrr: mrrData.data?.mrr || 89450,
        tickets: ticketsStats.data || { total: 156, open: 23, critical: 3 }
      })

      // Top Companies (mock enhanced com dados reais quando disponíveis)
      const companies = companiesData.data || []
      const topCompaniesData = companies.slice(0, 5).map((c: any, i: number) => ({
        id: c.id,
        name: c.name || `Empresa ${i + 1}`,
        employees: c.employee_count || Math.floor(Math.random() * 500) + 50,
        plan: c.plan || ['Starter', 'Business', 'Enterprise'][Math.floor(Math.random() * 3)],
        mrr: `R$ ${((c.mrr || Math.floor(Math.random() * 5000) + 500)).toLocaleString('pt-BR')}`
      }))

      if (topCompaniesData.length === 0) {
        // Dados de demonstração
        setTopCompanies([
          { id: '1', name: 'ContruBem S.A.', employees: 450, plan: 'Enterprise', mrr: 'R$ 4.500' },
          { id: '2', name: 'TeleNord Comunicações', employees: 280, plan: 'Business', mrr: 'R$ 2.800' },
          { id: '3', name: 'Hospital Santa Cruz', employees: 520, plan: 'Enterprise', mrr: 'R$ 5.200' },
          { id: '4', name: 'Indústria MetalMax', employees: 180, plan: 'Business', mrr: 'R$ 1.800' },
          { id: '5', name: 'Energia Solar Plus', employees: 95, plan: 'Starter', mrr: 'R$ 950' },
        ])
      } else {
        setTopCompanies(topCompaniesData)
      }

      // Recent Activities (de alertas reais ou mock)
      const alerts = alertsData.data || []
      const activities: Activity[] = alerts.slice(0, 5).map((a: any, i: number) => ({
        id: a.id || `${i}`,
        type: a.tipo || 'alert',
        message: a.mensagem || `Alerta #${i + 1}`,
        time: a.created_at ? formatTimeAgo(new Date(a.created_at)) : `${(i + 1) * 5} min atrás`,
        status: a.severity === 'critical' ? 'error' : a.severity === 'high' ? 'warning' : 'info'
      }))

      if (activities.length === 0) {
        setRecentActivities([
          { id: '1', type: 'company', message: 'Nova empresa cadastrada: TechCorp Ltda', time: '5 min atrás', status: 'success' },
          { id: '2', type: 'payment', message: 'Pagamento confirmado: ContruBem S.A.', time: '12 min atrás', status: 'success' },
          { id: '3', type: 'support', message: 'Ticket #1234 aberto: Problema com EPI', time: '25 min atrás', status: 'warning' },
          { id: '4', type: 'ml', message: 'Modelo de detecção atualizado v2.4.1', time: '1h atrás', status: 'info' },
          { id: '5', type: 'alert', message: 'Sistema de backup concluído', time: '2h atrás', status: 'success' },
        ])
      } else {
        setRecentActivities(activities)
      }

      // MRR Trend (últimos 6 meses)
      const months = ['Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      const mrrBase = stats.mrr || 89450
      setMrrTrend(months.map((month, i) => ({
        month,
        mrr: Math.round(mrrBase * (0.7 + (i * 0.06)))
      })))

      // User Growth (últimos 7 dias)
      const days = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
      setUserGrowth(days.map((day) => ({
        day,
        users: Math.round(50 + Math.random() * 30)
      })))

    } catch (error) {
      console.error('Erro ao carregar dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await loadDashboardData()
    setIsRefreshing(false)
  }

  const formatTimeAgo = (date: Date) => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return 'agora'
    if (diffMins < 60) return `${diffMins} min atrás`
    
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h atrás`
    
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d atrás`
  }

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      violet: { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20' },
      cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/20' },
      emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
      amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
      rose: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
    }
    return colors[color] || colors.violet
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-400" />
      case 'error': return <AlertTriangle className="w-4 h-4 text-rose-400" />
      case 'info': return <Activity className="w-4 h-4 text-violet-400" />
      default: return <Clock className="w-4 h-4 text-slate-400" />
    }
  }

  const statsCards = [
    {
      label: 'Empresas Ativas',
      value: stats.companies.active.toLocaleString('pt-BR'),
      change: `+${stats.companies.thisMonth}`,
      changeLabel: 'este mês',
      changeType: 'positive',
      icon: Building2,
      color: 'violet',
    },
    {
      label: 'Usuários Totais',
      value: stats.users.total.toLocaleString('pt-BR'),
      change: `${stats.users.active}`,
      changeLabel: 'ativos',
      changeType: 'positive',
      icon: Users,
      color: 'cyan',
    },
    {
      label: 'MRR',
      value: `R$ ${stats.mrr.toLocaleString('pt-BR')}`,
      change: '+15%',
      changeLabel: 'vs mês anterior',
      changeType: 'positive',
      icon: CreditCard,
      color: 'emerald',
    },
    {
      label: 'Tickets Abertos',
      value: stats.tickets.open.toString(),
      change: stats.tickets.critical > 0 ? `${stats.tickets.critical} críticos` : 'Nenhum crítico',
      changeLabel: '',
      changeType: stats.tickets.critical > 0 ? 'negative' : 'positive',
      icon: AlertTriangle,
      color: stats.tickets.critical > 0 ? 'rose' : 'amber',
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Carregando dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Visão geral do sistema WorkStream</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon
          const colors = getColorClasses(stat.color)

          return (
            <div key={stat.label} className="admin-stat group">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
                  <Icon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium ${
                  stat.changeType === 'positive' ? 'text-emerald-400' : 'text-rose-400'
                }`}>
                  {stat.changeType === 'positive' ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {stat.change}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
                {stat.changeLabel && (
                  <p className="text-xs text-slate-500 mt-1">{stat.changeLabel}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MRR Trend */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Evolução MRR
            </h2>
            <span className="text-xs text-slate-500">Últimos 6 meses</span>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={mrrTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                formatter={(value: number) => [`R$ ${value.toLocaleString('pt-BR')}`, 'MRR']}
              />
              <Area
                type="monotone"
                dataKey="mrr"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Novos Usuários
            </h2>
            <span className="text-xs text-slate-500">Últimos 7 dias</span>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={userGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis dataKey="day" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
              />
              <Bar dataKey="users" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Atividade Recente</h2>
            <button className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1">
              Ver tudo
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center gap-4 p-3 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="p-2 rounded-lg bg-slate-800">
                  {getStatusIcon(activity.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 truncate">{activity.message}</p>
                  <p className="text-xs text-slate-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Companies */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Top Empresas</h2>
            <button className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1">
              Ver todas
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {topCompanies.map((company, index) => (
              <div
                key={company.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-violet-500/20 flex items-center justify-center text-sm font-bold text-violet-400">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{company.name}</p>
                  <p className="text-xs text-slate-500">{company.employees} colaboradores • {company.plan}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-emerald-400">{company.mrr}</p>
                  <p className="text-xs text-slate-500">/mês</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="admin-card">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-violet-400" />
          Status dos Sistemas
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'API Principal', status: 'online', latency: '45ms', icon: Zap },
            { name: 'Banco de Dados', status: 'online', latency: '12ms', icon: Database },
            { name: 'ML Pipeline', status: 'online', latency: '234ms', icon: Cpu },
            { name: 'Storage', status: 'online', latency: '89ms', icon: HardDrive },
          ].map((service) => {
            const Icon = service.icon
            return (
              <div key={service.name} className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-medium text-emerald-400 uppercase">{service.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-500" />
                  <p className="text-sm font-medium text-white">{service.name}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">Latência: {service.latency}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 text-center">
          <p className="text-2xl font-bold text-white">{stats.employees.total.toLocaleString('pt-BR')}</p>
          <p className="text-xs text-slate-500">Colaboradores Monitorados</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 text-center">
          <p className="text-2xl font-bold text-white">99.9%</p>
          <p className="text-xs text-slate-500">Uptime (30 dias)</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 text-center">
          <p className="text-2xl font-bold text-white">2.3%</p>
          <p className="text-xs text-slate-500">Churn Rate</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 text-center">
          <p className="text-2xl font-bold text-white">R$ {(stats.mrr * 12 / 1000).toFixed(0)}k</p>
          <p className="text-xs text-slate-500">ARR Projetado</p>
        </div>
      </div>
    </div>
  )
}
